import React from 'react';
import {connect} from 'react-redux';
import {Field, Form, reduxForm} from "redux-form";
import {FormattedMessage} from "react-intl";

/*** SECTIONS ***/
import CompanyInformation from "./Sections/CompanyInformation";
import BillingInformation from "./Sections/BillingInformation";
import BusinessAddress from "./Sections/BusinessAddress";
import ShipFromAddress from './Sections/ShipFromAddress';
import DepositsAndDisclaimers from "./Sections/DepositsAndDisclaimers";
import FabricatorAccountSettings from "./Sections/FabricatorAccountSettings";
import PingWidgetSection from "./Sections/PingWidgetSection";
import Analytics from "./Sections/Analytics";
import DocusignAndFingerSignature from "./Sections/DocusignAndFingerSignature";
import GeneralSettings from "./Sections/GeneralSettings";
import ToolSubscription from "./Sections/ToolSubscription";
import CompanyLogo from "./Sections/CompanyLogo";
import CustomerAdditionalInfo from "./Sections/CustomerAdditionalInfo";
import ToolSettings from './Sections/ToolSettings';
/*** SECTIONS END ***/

import {modifyPageLayoutComponents} from '../PageLayout';
import AccountPageHeader from './AccountPageHeader';
import AccountPageFooter from "./AccountPageFooter";
import {ACCOUNT_SECTIONS} from "./Sections/sectionNames";
import {
  setAccountPageInfo,
  setSubscriptionsInfo,
  toggleAllSectionsOpened,
  toggleFormSubmitted
} from "./AccountActions";
import {AccessRights, PARTS} from "../../schemas/accessRightsModel";
import {getInfoByClientType} from "./accountPageFunctions";
import {
  clearInitialValues,
  getCustomerInfo,
  getSubscriptions,
  showESignatureOverlay
} from "../AddNewEntity/AddNewEntityActions";
import {HiddenField} from "./FormField";
import AccountPageTopPanel from "./AccountPageTopPanel";
// import ESignatureWrapper from "./ESignatureWrapper";
import ReceiptSettingsSection from "./Sections/ReceiptSettingsSection";

class AccountPage extends React.Component {

  componentDidMount() {
    this.setAccountPageInfo();
  }

  componentWillUnmount() {
    this.props.clearInitialValues();
  }

  setAccountPageInfo = () => {
    const {clientType, clientSubType, entityId} = this.props.location.query;
    const clientInfo = getInfoByClientType(clientType, clientSubType);

    const {AR} = this.props;
    const isEmployee = clientType === 'Customer';
    const isCustomer = clientType === 'Client';
    const isFabricator = clientType === 'Fabricator' && !clientSubType;
    const isLicensedFabricator = AR.isFabAdmin() && !AR.isFabMaster();
    const isStore = clientType === 'Fabricator' && clientSubType === 'Store';
    const isMasterFabricator = AR.isSiteAdmin(false) || AR.isFabMaster();
    const urlOnSave = AR.isClient() ? "/" : clientInfo.url;

    let canEditFields = true;
    let canEditPhone2 = true;
    let canChangePassword = false;
    let canEditCompanyName = false;
    let canEditPrimaryEmail = (AR.isSiteAdmin(false) || AR.isFabAdmin() || AR.isFabMaster() || (AR.isFabUser() && isCustomer));

    const canEditName = AR.isSiteAdmin(false) || !entityId || AR.v(PARTS.HUB_BUTTON_CUSTOMERS);

    if (!isFabricator && !isStore) {
      if (entityId) {
        canChangePassword = !AR.isClient();
      }
    }

    if (entityId) {
      if (clientType === 'Fabricator') {
        canEditFields = AR.isSiteAdmin(false) || AR.isFabMaster() || AR.v(PARTS.HUB_BUTTON_FABRICATORS);
        canEditCompanyName = AR.isSiteAdmin(false) || AR.isFabMaster() || AR.v(PARTS.HUB_BUTTON_FABRICATORS);
      } else if (clientType === 'Client') {
        canEditFields = !AR.isClient();
        canEditPhone2 = !AR.isClient() || AR.isMe(entityId, false);
        canEditCompanyName = !AR.isClient();
      }
    } else {
      switch (clientType) {
        case 'Client':
        case 'Fabricator':
          canEditCompanyName = true;
          break;
        default:
          break;
      }
    }

    const canAddFabricator = AR.isSiteAdmin(false) || AR.isFabAdmin() || AR.isFabMaster();
    this.props.setAccountPageInfo({
      entityId,
      clientType: clientType || 'Client',
      clientSubType: clientSubType || null,
      entityType: isEmployee ? 'employee' : (isCustomer ? 'customer' : (isFabricator ? 'fabricator' : 'user')),
      isNew: !entityId,
      isEmployee,
      isCustomer,
      isFabricator,
      isLicensedFabricator,
      isStore,
      isMasterFabricator,
      isSiteAdmin: AR.isSiteAdmin(),
      canAddFabricator,
      canChangePassword,
      canEditCompanyName,
      canEditFields,
      canEditName,
      canEditPhone2,
      canEditPrimaryEmail,
      urlOnSave
    });
    this.props.setSubscriptionsInfo({
      fabricatorNeedToSignAgreement: false // Commented out on 1/29/2024 as we are no longer using this form - !entityId && isFabricator // when creating a new fabricator an agreement must be signed
    })

    entityId ? this.props.getCustomerInfo(entityId, clientType, clientSubType) : this.props.getSubscriptions();
  }

  onSubmit = () => {
    this.props.toggleFormSubmitted(true);
  }

  render() {

    if (!this.props.accountPageInfoIsSet) {
      return null
    }

    const allSectionsExpanded = Object.keys(ACCOUNT_SECTIONS).every(sectionKey => {
      return this.props.sectionsOpened.includes(sectionKey)
    })
    const {isEmployee, isFabricator, isCustomer} = this.props.accountPageInfo;
    const {AR, subscriptionsInfo} = this.props;
    const isSiteAdmin = AR.isSiteAdmin(false);
    const hasEnterpriseSubscription = subscriptionsInfo.kitchenProgramType === "enterprise"
      || subscriptionsInfo.bathroomProgramType === "enterprise";

    return (
      <div className="acc">
        <AccountPageTopPanel/>
        <button className={"acc-expand" + (allSectionsExpanded ? " acc-expand--all-expanded" : "")}
                onClick={() => this.props.toggleAllSectionsOpened(!allSectionsExpanded)}>
          <FormattedMessage id={allSectionsExpanded ? "account.collapseButton" : "account.expandButton"}/>
        </button>
        <Form onSubmit={() => this.onSubmit()}>
          <Field name="id" component={HiddenField} label="ID Number" disabled={true}/>
          <CompanyInformation dispatch={this.props.dispatch}/>
          {isFabricator ? <CompanyLogo/> : null}
          {isCustomer ? <CustomerAdditionalInfo/> : null}
          {!isEmployee ? <BusinessAddress/> : null}
          {isSiteAdmin && !isEmployee && !isCustomer ? <ShipFromAddress/> : null}
          {!isEmployee ? <BillingInformation/> : null}
          {isFabricator && (AR.v(PARTS.FAB_TOGGLE_BUTTON_DEPOSIT) || AR.isSiteAdmin()) ? <DepositsAndDisclaimers/> : null}
          {isFabricator ? <FabricatorAccountSettings/> : null}
          {isFabricator ? <Analytics/> : null}
          {isFabricator ? <PingWidgetSection/> : null}
          {isFabricator ? <ToolSubscription tool="bathroom"/> : null}
          {isFabricator ? <ToolSubscription tool="kitchen"/> : null}
          {isFabricator ? <ToolSubscription tool="fireplace"/> : null}
          {isFabricator ? <ToolSubscription tool="laundryroom"/> : null}
          {isFabricator ? <ToolSubscription tool="leadform"/> : null}
          {isFabricator ? <ReceiptSettingsSection/> : null}
          {isFabricator && hasEnterpriseSubscription ? <DocusignAndFingerSignature/> : null}
          {isSiteAdmin && !isEmployee && !isCustomer ? <ToolSettings tool="bathroom"/> : null}
          {isSiteAdmin && !isEmployee && !isCustomer ? <ToolSettings tool="kitchen"/> : null}
          {isSiteAdmin && !isEmployee && !isCustomer ? <ToolSettings tool="fireplace"/> : null}
          {isSiteAdmin && !isEmployee && !isCustomer ? <ToolSettings tool="laundryroom"/> : null}
          {isFabricator ? <GeneralSettings AR={AR}/> : null}
        </Form>
        {/*<ESignatureWrapper/>*/}
      </div>
    )
  }

}

const mapStateToProps = (store) => {
  const {sectionsOpened, accountPageInfo, subscriptionsInfo, accountPageInfoIsSet} = store.accountState;
  return {
    initialValues: store.newaccount.customerInfo,
    enableReinitialize: true, // http://redux-form.com/6.6.0/examples/initializeFromState/ p.1 // this sets form values on user receive data
    sectionsOpened,
    accountPageInfoIsSet,
    accountPageInfo,
    subscriptionsInfo,
    AR: AccessRights.get(store),
    asCustomer: store.asCustomer
  };
};

const mapDispatchToProps = {
  toggleAllSectionsOpened,
  setAccountPageInfo,
  getCustomerInfo,
  showESignatureOverlayFn: showESignatureOverlay,
  setSubscriptionsInfo,
  getSubscriptions,
  clearInitialValues,
  toggleFormSubmitted
};

const connector = connect(mapStateToProps, mapDispatchToProps);

const AccountPageForm = reduxForm({form: 'newEntity'})(AccountPage);
const AccountPageFooterForm = reduxForm({form: 'newEntity'})(AccountPageFooter);

export const AccountPageLayoutComponents = modifyPageLayoutComponents({
  pageHeader: AccountPageHeader,
  content: connector(AccountPageForm),
  pageFooter: AccountPageFooterForm
});

